<script lang="ts">
  import { onMount } from 'svelte';
  import { clients } from '../lib/stores';
  import {
    getSequences, getSequence, createSequence, updateSequence,
    activateSequence, deactivateSequence, deleteSequence,
    getClientContacts,
    type Sequence, type SequenceEmail,
  } from '../lib/api';

  let sequences = $state<Sequence[]>([]);
  let loading = $state(true);
  let expandedClient = $state('');

  // Create form
  let showCreate = $state(false);
  let createClient = $state('');
  let createEmail = $state('');
  let createName = $state('');
  let createBrief = $state('');
  let createDelay2 = $state(3);
  let createDelay3 = $state(7);
  let generating = $state(false);
  let clientContacts = $state<string[]>([]);

  // Edit mode
  let editingClient = $state('');
  let editEmails = $state<SequenceEmail[]>([]);

  // Confirm delete
  let confirmDelete = $state('');

  onMount(async () => {
    await loadSequences();
  });

  async function loadSequences() {
    loading = true;
    try {
      sequences = await getSequences();
    } catch { /* */ }
    loading = false;
  }

  async function loadContacts(client: string) {
    // Discard a late reply for a client the user has already moved off. Two quick
    // changes of the Client select leave two requests in flight, and if the first
    // resolves last the contact list shown belongs to the PREVIOUS client while
    // `createClient` reads as the new one — handleCreate posts that pair, so the
    // sequence goes to the wrong prospect. Same fault as Prospecting.onClientChange.
    try {
      const contacts = await getClientContacts(client);
      if (client !== createClient) return;
      clientContacts = contacts;
    } catch {
      if (client !== createClient) return;
      clientContacts = [];
    }
  }

  async function handleCreate() {
    if (!createClient || !createEmail || !createBrief || generating) return;
    generating = true;
    try {
      const seq = await createSequence({
        client: createClient,
        contact_email: createEmail,
        contact_name: createName,
        brief: createBrief,
        delay_2: createDelay2,
        delay_3: createDelay3,
      });
      sequences = [...sequences.filter(s => s.client !== createClient), seq];
      showCreate = false;
      // Auto-expand to review
      expandedClient = createClient;
      editingClient = createClient;
      editEmails = [...seq.emails];
    } catch (e: any) {
      alert(e.message || 'Failed to generate sequence');
    } finally {
      generating = false;
    }
  }

  async function handleSaveEdits(client: string) {
    try {
      await updateSequence(client, editEmails);
      editingClient = '';
      await loadSequences();
    } catch (e: any) {
      alert(e.message || 'Failed to save');
    }
  }

  async function handleActivate(client: string) {
    await activateSequence(client);
    await loadSequences();
  }

  async function handleDeactivate(client: string) {
    await deactivateSequence(client);
    await loadSequences();
  }

  async function handleDelete(client: string) {
    if (confirmDelete !== client) {
      confirmDelete = client;
      setTimeout(() => { if (confirmDelete === client) confirmDelete = ''; }, 3000);
      return;
    }
    await deleteSequence(client);
    sequences = sequences.filter(s => s.client !== client);
    confirmDelete = '';
    if (expandedClient === client) expandedClient = '';
  }

  function statusColor(status: string): string {
    if (status === 'sent') return 'var(--green, #4ade80)';
    if (status === 'skipped') return 'var(--text-dim, #666)';
    return 'var(--blue, #60a5fa)';
  }

  function sequenceStatus(seq: Sequence): { label: string; color: string } {
    if (seq.active) return { label: 'Active', color: 'var(--green, #4ade80)' };
    const allDone = seq.emails.every(e => e.status !== 'pending');
    if (allDone && seq.emails.some(e => e.status === 'skipped'))
      return { label: 'Aborted (reply)', color: 'var(--orange, #f59e0b)' };
    if (allDone) return { label: 'Completed', color: 'var(--text-dim)' };
    return { label: 'Draft', color: 'var(--blue, #60a5fa)' };
  }
</script>

<div class="page-title">Outreach Sequences</div>
<div class="page-subtitle">Multi-step email campaigns with auto-advancement</div>

<div class="mb-3">
  <button onclick={() => { showCreate = !showCreate; if (showCreate) { createClient = ''; createEmail = ''; createName = ''; createBrief = ''; } }}>
    {showCreate ? 'Cancel' : 'New Sequence'}
  </button>
</div>

{#if showCreate}
  <div class="card mb-3">
    <div class="font-bold mb-2">Create Sequence</div>

    <div class="form-row">
      <label for="sq-createClient" class="text-xs text-dim">Client</label>
      <select id="sq-createClient" bind:value={createClient} onchange={() => createClient && loadContacts(createClient)}>
        <option value="">Select client...</option>
        {#each $clients as c}
          <option value={c}>{c.replace(/_/g, ' ')}</option>
        {/each}
      </select>
    </div>

    <div class="form-row">
      <label for="sq-createEmail" class="text-xs text-dim">Contact Email</label>
      {#if clientContacts.length > 0}
        <select id="sq-createEmail" bind:value={createEmail}>
          <option value="">Select or type...</option>
          {#each clientContacts as e}
            <option value={e}>{e}</option>
          {/each}
        </select>
      {/if}
      <input type="email" placeholder="contact@company.com" bind:value={createEmail} />
    </div>

    <div class="form-row">
      <label for="sq-createName" class="text-xs text-dim">Contact Name (optional)</label>
      <input id="sq-createName" type="text" placeholder="John Smith" bind:value={createName} />
    </div>

    <div class="form-row">
      <label for="sq-createBrief" class="text-xs text-dim">Campaign Brief</label>
      <textarea id="sq-createBrief"
        bind:value={createBrief}
        placeholder="e.g. PDAC follow-up, emphasize free POC and 2-week turnaround"
        rows="3"
        style="width:100%;"
      ></textarea>
    </div>

    <div class="form-row" style="display:flex; gap:16px;">
      <div>
        <label for="sq-createDelay2" class="text-xs text-dim">Email 2 delay (days)</label>
        <input id="sq-createDelay2" type="number" min="1" max="14" bind:value={createDelay2} style="width:70px;" />
      </div>
      <div>
        <label for="sq-createDelay3" class="text-xs text-dim">Email 3 delay (days)</label>
        <input id="sq-createDelay3" type="number" min="2" max="30" bind:value={createDelay3} style="width:70px;" />
      </div>
    </div>

    <button
      onclick={handleCreate}
      disabled={!createClient || !createEmail || !createBrief || generating}
      style="margin-top:10px;"
    >
      {generating ? 'Generating 3 emails...' : 'Generate Sequence'}
    </button>
  </div>
{/if}

{#if loading}
  <div class="text-dim">Loading sequences...</div>
{:else if sequences.length === 0}
  <div class="empty-state">
    <div class="empty-state-icon">📧</div>
    <div class="empty-state-text">No sequences yet. Create one to start a multi-step outreach campaign.</div>
  </div>
{:else}
  {#each sequences as seq}
    {@const status = sequenceStatus(seq)}
    <div class="card mb-2">
      <div
        class="seq-header"
        role="button" tabindex="0"
        onclick={() => expandedClient = expandedClient === seq.client ? '' : seq.client}
      >
        <span class="seq-status-dot" style="background: {status.color};"></span>
        <span class="font-bold">{seq.client.replace(/_/g, ' ')}</span>
        <span class="text-xs text-dim">{seq.contact_email}</span>
        <span class="text-xs" style="color: {status.color};">{status.label}</span>
        <span class="text-xs text-dim" style="margin-left:auto;">
          {seq.emails.filter(e => e.status === 'sent').length}/{seq.emails.length} sent
        </span>
      </div>

      {#if expandedClient === seq.client}
        <div class="seq-detail">
          <div class="text-xs text-dim mb-2">Brief: {seq.brief}</div>

          <div class="seq-timeline">
            {#each seq.emails as email, i}
              <div class="seq-email" class:editing={editingClient === seq.client}>
                <div class="seq-email-header">
                  <span class="seq-step" style="border-color: {statusColor(email.status)};">
                    {email.step}
                  </span>
                  <span class="text-xs">Day {email.delay_days}</span>
                  <span class="seq-email-status" style="color: {statusColor(email.status)};">
                    {email.status}
                  </span>
                  {#if email.sent_at}
                    <span class="text-xs text-dim">{email.sent_at.slice(0, 16)}</span>
                  {/if}
                </div>

                {#if editingClient === seq.client}
                  <input
                    class="seq-edit-subject"
                    value={editEmails[i]?.subject || ''}
                    oninput={(e) => { editEmails[i] = { ...editEmails[i], subject: (e.target as HTMLInputElement).value }; }}
                    placeholder="Subject"
                  />
                  <textarea
                    class="seq-edit-body"
                    value={editEmails[i]?.body || ''}
                    oninput={(e) => { editEmails[i] = { ...editEmails[i], body: (e.target as HTMLTextAreaElement).value }; }}
                    rows="5"
                  ></textarea>
                {:else}
                  <div class="seq-email-subject">{email.subject}</div>
                  <pre class="seq-email-body">{email.body}</pre>
                {/if}
              </div>
            {/each}
          </div>

          <div class="seq-actions">
            {#if editingClient === seq.client}
              <button onclick={() => handleSaveEdits(seq.client)}>Save Changes</button>
              <button onclick={() => editingClient = ''}>Cancel</button>
            {:else}
              {#if !seq.active && seq.emails.some(e => e.status === 'pending')}
                <button onclick={() => { editingClient = seq.client; editEmails = seq.emails.map(e => ({...e})); }}>
                  Edit
                </button>
                <button onclick={() => handleActivate(seq.client)} style="color: var(--green, #4ade80); border-color: var(--green, #4ade80);">
                  Activate
                </button>
              {/if}
              {#if seq.active}
                <button onclick={() => handleDeactivate(seq.client)}>Pause</button>
              {/if}
              <button
                onclick={() => handleDelete(seq.client)}
                style="color: var(--red, #f87171); border-color: var(--red, #f87171);"
              >
                {confirmDelete === seq.client ? 'Confirm Delete?' : 'Delete'}
              </button>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  {/each}
{/if}

<style>
  .form-row {
    margin-bottom: 10px;
  }
  .form-row label {
    display: block;
    margin-bottom: 4px;
  }
  .seq-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    cursor: pointer;
  }
  .seq-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .seq-detail {
    padding: 10px 0;
    border-top: 1px solid var(--border, #333);
  }
  .seq-timeline {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
  }
  .seq-email {
    padding: 14px 16px;
    border: 1px solid var(--border, #333);
    border-radius: 8px;
    background: var(--surface, #1e1e2e);
  }
  .seq-email-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }
  .seq-step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    color: var(--text, #e0e0e0);
  }
  .seq-email-status {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .seq-email-subject {
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 6px;
    color: var(--text, #e0e0e0);
  }
  .seq-email-body {
    font-family: inherit;
    font-size: 12.5px;
    line-height: 1.6;
    color: var(--text, #e0e0e0);
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid var(--border, #333);
    border-radius: 6px;
    max-height: 250px;
    overflow-y: auto;
  }
  .seq-edit-subject {
    width: 100%;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
    border: 1px solid var(--blue, #60a5fa);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    outline: none;
  }
  .seq-edit-body {
    width: 100%;
    padding: 10px 12px;
    font-size: 12.5px;
    line-height: 1.6;
    border: 1px solid var(--blue, #60a5fa);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    resize: vertical;
    outline: none;
  }
  .seq-actions {
    display: flex;
    gap: 8px;
  }
</style>
